/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM, useTranslate } from "@adapptio/lib-components";
import { offEvent, onEvent } from "@adapptio/lib-shared";
import React, { FC, useCallback, useContext, useState, useEffect, useRef } from "react";
import { termsEditorCommon } from "../../../terms/common";
import { EditorMainContext } from "../../contexts/EditorMainContext";
import { useEscKey } from "../../hooks/useEscKey";
import { FC_ANCHOR, FC_DIRECTION } from "../FloatingContainer";
import { Icon, ICON_SIZE } from "../Icon";
import { RouteList } from "../RouteList";
import { FieldFloatingPanel, FIELD_FLOATING_PANEL_STYLE } from "./FieldFloatingPanel";
import { IFieldValidationError } from "./FieldTypes";
import { FieldWrapper } from "./FieldWrapper";
import { FieldValidationErrors } from "./FieldValidationErrors";

export interface IFieldRouteSelectorProps {
	value: string;
	placeholder?: string;
	readOnly?: boolean;
	validationErrors?: IFieldValidationError[];
	className?: string;
	grow?: boolean;
	listAnchor?: FC_ANCHOR;
	listDirection?: FC_DIRECTION;
	listOffset?: number;
	onChange?: (value: string) => void;
}

export const FieldRouteSelector: FC<IFieldRouteSelectorProps> = ({
	value,
	placeholder,
	readOnly,
	validationErrors,
	className,
	grow,
	listAnchor,
	listDirection,
	listOffset,
	onChange
}) => {
	const editorCtx = useContext(EditorMainContext);
	const t = useTranslate();

	const [ isListOpened, setIsListOpened ] = useState(false);
	const elRef = useRef<HTMLButtonElement>();

	const [ label, setLabel ] = useState(() => {
		return value
			? editorCtx.resourceService.getRouteByName(value)?.key ||
					t("editor", termsEditorCommon.fields.routeSelector.notFound)
			: t("editor", placeholder) || t("editor", termsEditorCommon.fields.routeSelector.select);
	});

	const toggleRouteList = useCallback(() => {
		setIsListOpened((x) => !x);
	}, []);

	const closeRouteList = useCallback(() => {
		setIsListOpened(false);
	}, []);

	const handleSelect = useCallback(
		(routeName: string) => {
			onChange(routeName);
			setIsListOpened(false);
		},
		[ onChange ]
	);

	useEscKey(() => setIsListOpened(false), isListOpened);

	useEffect(() => {
		if (value) {
			const handleChange = () => {
				const route = editorCtx.resourceService.getRouteByName(value);
				setLabel(route ? route.key : t("editor", termsEditorCommon.fields.routeSelector.notFound));
			};

			handleChange();

			onEvent(editorCtx.resourceService.onRouteListChange, handleChange);

			return () => {
				offEvent(editorCtx.resourceService.onRouteListChange, handleChange);
			};
		} else {
			setLabel(t("editor", placeholder) || t("editor", termsEditorCommon.fields.routeSelector.select));
		}
	}, [ editorCtx.resourceService, value ]);

	const baseClass = BEM("edt-field", "selector", {
		"is-empty": !value,
		"is-opened": isListOpened,
		readonly: readOnly
	});

	baseClass.add("edt-field-control");

	return (
		<FieldWrapper type="route-selector" className={className} grow={grow} isFocused={isListOpened}>
			<button
				className={baseClass.toClassName()}
				onClick={toggleRouteList}
				disabled={readOnly}
				ref={elRef}
			>
				<span className={baseClass.element("selector-label").toClassName()}>{label}</span>
				<FieldValidationErrors errors={validationErrors} />
				<Icon name="mdi/folder-open" size={ICON_SIZE.CUSTOM} />
			</button>
			<FieldFloatingPanel
				for={elRef}
				isOpened={isListOpened}
				anchor={listAnchor || FC_ANCHOR.RIGHT_BOTTOM}
				direction={listDirection || FC_DIRECTION.HR}
				style={FIELD_FLOATING_PANEL_STYLE.STICKY}
				offset={listOffset || 0}
				className="edt-field--type-route-selector__panel"
				onClose={closeRouteList}
			>
				<RouteList
					resourceService={editorCtx.resourceService}
					selectedRouteKey={value}
					onSelect={handleSelect}
				/>
			</FieldFloatingPanel>
		</FieldWrapper>
	);
};
